import { startWorkflow, ErrorWithSetting } from "./startWorkflow";
import { registrationStore, type RegistrationStore } from "../stores/registration";
import Fooji from "@fooji/microsites";
import { setFlowStep, setPage } from "../stores/route";
import { notify } from "./bugsnag";
import { setCountry } from "../stores/country";
import { jwtDecode } from 'jwt-decode';
import { campaignCountries, allSupportedCountries } from "../constants/countries";
import type { SupportedCountryCode } from "../constants/countries";

export const saveJwt = (jwt: string) => {
  localStorage.setItem("jwt", jwt);
  sessionStorage.setItem("jwt", jwt);
}

export const getUserId = () => {
  const jwt = sessionStorage.getItem("jwt");
  const decodedJwt = jwtDecode(jwt!) as { id: number };
  return decodedJwt.id;
}

const handleUnsupportedCountry = (country: SupportedCountryCode) => {
  if (!allSupportedCountries.includes(country)) {
    return setPage("error", "unsupportedCountry");
  }

  return setPage("error", "campaignNotSupported");
}

export const getJwtCountry = (jwt: string) => {
  const decodedJwt = jwtDecode(jwt) as { fooji_data: { activision_country: string } };
  return decodedJwt.fooji_data.activision_country as SupportedCountryCode;
}

const saveJwtCountry = () => {
  const jwt = sessionStorage.getItem("jwt");

  if (jwt) {
    const country = getJwtCountry(jwt);
    setCountry(country);
  }
}

export const saveAuthData = async () => {
  const hash = window.location.hash.substring(1); // removes the #
  const hashParams = new URLSearchParams(hash);

  try {
    const jwt = hashParams.get("jwt") as string;
    const country = getJwtCountry(jwt);

    if (!campaignCountries.includes(country)) {
      return handleUnsupportedCountry(country);
    }

    saveJwt(jwt);

    await saveUserAndStartWorkflow();

    setFlowStep("register");
  } catch (error) {
    console.error("Error updating user data after authentication", error);
    const isHandledError = error instanceof ErrorWithSetting && error.errorSetting && error.errorSetting !== "generic";

    if (!isHandledError) {
      notify({
        error,
        errorMessage: "Error updating user data after authentication",
        additionalMetadata: { jwtQueryParam: hashParams.get("jwt") }
      });
    }

    setPage("error", (error instanceof ErrorWithSetting) ? error.errorSetting : "generic");
  }
};

export const saveUserAndStartWorkflow = async () => {
  saveJwtCountry();

  const [userData] = await Promise.all([
    Fooji.User.get(),
    await startWorkflow()
  ]);

  const additionalRegistrationData: Partial<RegistrationStore> = {};

  if (userData?.dob) {
    const dob = (userData.dob as string).split("-");
    additionalRegistrationData.dob = {
      month: String(Number(dob[1])), // to remove leading zero
      day: String(Number(dob[2])), // to remove leading zero
      year: dob[0]
    };
  }

  if (userData?.console_platform) {
    additionalRegistrationData.consoleOption = userData.console_platform as string;
  }

  registrationStore.update((state) => ({
    ...state,
    ...additionalRegistrationData,
    firstName: userData?.first_name ?? "",
    lastName: userData?.last_name ?? "",
    email: userData?.email ?? "",
    phone: userData?.phone ?? "", // Only used to allow swapping on the verify modal
    lockedEmail: Boolean(userData?.email)
  }));
}
